import React, { useState, useEffect, useRef, useMemo } from "react";
import "quill-mention";
import { Button, Modal, Input } from "../../components/common";
import _ from "lodash";
import Image from "../../components/common/Image";
import httpRequest from "../../utils/httpRequest";
import API_URLS from "../../utils/apiUrls";
import FlashMessage from "../../components/common/FlashMessage";
import { useNavigate, useParams } from "react-router-dom";
import appRoutes from "../../routes/app.routes";
import { Delete, Edit } from "@material-ui/icons";
import ArticleDropZone from "../../components/common/ArticleDropZone";
import { Menu, MenuItem } from "@material-ui/core";
import SEOAIGenerator from "./SeoAiGenrater";
import aiGenrateIcon from "../../assets/svg/ai-icon-generate.svg";
import ArticleTextEditor from "../../components/common/ArticleTextEditor";
import { useSelector } from "react-redux";
import Avatar from "../../components/common/AvtarImage";
import { rootReducersState } from "../../reducers";
// import { ReactComponent as SettingsIcon } from "../../assets/svg/setting-white.svg";

type EditHistory = {
  filterBrightness: number;
  filterContrast: number;
  filterSaturate: number;
  filterGrayscale: number;
  adjustBrightness: number;
  adjustContrast: number;
  adjustSaturate: number;
  adjustGrayscale: number;
  rotate: number;
  flipHorizontal: boolean;
  flipVertical: boolean;
  zoom: number;
  straighten: number;
  selectedFilter: string;
};

interface ArticleType {
  seo_title: string;
  seo_description: string;
  title: string;
  description: string;
  focus_keyword?: string;
  slug?: string;
}

interface MediaItem {
  id: string;
  file: File;
  blobUrl: string;
  type: "image" | "video";
  insertIndex: number;
  timestamp: number;
  thumbnail?: string;
}

const articleSnapshot = (
  article: ArticleType,
  caption: string,
  coverUrl: string,
  coverMediaType: "image" | "video" | null
) => JSON.stringify({
  title: article.title || "",
  description: article.description || "",
  seo_title: article.seo_title || "",
  seo_description: article.seo_description || "",
  focus_keyword: article.focus_keyword || "",
  slug: article.slug || "",
  caption: caption || "",
  coverUrl: coverUrl || "",
  coverMediaType: coverMediaType || "",
});

function Article() {
  const [cropPreviewImage, setPreviewImage] = useState<string>("");
  const [uploadImage, setUploadImage] = useState<Blob | File | undefined>();
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);
  const [showShareModal, setShowShareModal] = useState<boolean>(false);
  const [articleCaption, setArticleCaption] = useState<string>("");
  const [initialArticleSnapshot, setInitialArticleSnapshot] = useState<string | null>(null);
  const [articleData, setArticleData] = useState<ArticleType>({} as ArticleType);
  const [mediaType, setMediaType] = useState<"image" | "video" | null>(null);
  const [videoThumbnail, setVideoThumbnail] = useState<string>("");
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [kwAnchorEl, setKwAnchorEl] = useState<null | HTMLElement>(null);
  const [kwLoading, setKwLoading] = useState(false);
  const [kwList, setKwList] = useState<string[]>([]);
  const [selectedKw, setSelectedKw] = useState<string | null>(null);
  const [showSEOAIGenerator, setShowSEOAIGenerator] = useState<boolean>(false);
  const [seoGeneratorType, setSeoGeneratorType] = useState<"title" | "description">("title");
  const [variableMenuAnchor, setVariableMenuAnchor] = useState<null | HTMLElement>(null);
  const [activeVariableField, setActiveVariableField] = useState<"seo_title" | "seo_description" | null>(null);
  const [selectedVariableKey, setSelectedVariableKey] = useState<string | null>(null);

  const [originalImage, setOriginalImage] = useState<string>("");
  const [editHistory, setEditHistory] = useState<EditHistory | null>(null);

  const [contentMediaItems, setContentMediaItems] = useState<MediaItem[]>([]);
  const [prepareFormDataCallback, setPrepareFormDataCallback] = useState<((formData?: FormData) => FormData) | null>(null);

  const [contentMediaProcessed, setContentMediaProcessed] = useState<any>(null);
  const [initialMediaItems, setInitialMediaItems] = useState<MediaItem[]>([]);
  const sessionReducer = useSelector(
    ({ session }: rootReducersState) => session
  );
  const tmpCurrentUser = _.get(sessionReducer, "currentUser", {});
  const userName = _.get(tmpCurrentUser, "first_name", "") + " " + _.get(tmpCurrentUser, "last_name", "");
  const [fieldErrors, setFieldErrors] = useState<{
    seo_title?: string;
    seo_description?: string;
    focus_keyword?: string;
    slug?: string;
  }>({});
  // Older articles may have slugs that predate the current 75-character
  // limit. Do not block unrelated edits unless the user chooses to edit it.
  const [isSlugEdited, setIsSlugEdited] = useState(false);

  const limits = { seo_title: 60, seo_description: 160, focus_keyword: 60, slug: 75 };

  const params = useParams();
  const articleId = _.get(params, "id", "");
  const flag = articleId ? "update" : "create";
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isArticleDirty =
    flag === "create" ||
    (initialArticleSnapshot !== null &&
      initialArticleSnapshot !== articleSnapshot(articleData, articleCaption, cropPreviewImage, mediaType));

  const originalSetRef = useRef<boolean>(false);

  useEffect(() => {
    if (articleId) fetchArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  useEffect(() => {
    if (textareaRef.current && articleData.title) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [articleData.title]);

  // Capture the saved state after the rich-text editor has finished
  // normalizing the article HTML it receives from the API.
  useEffect(() => {
    if (
      flag !== "update" ||
      initialArticleSnapshot !== null ||
      !articleData.title ||
      !articleData.description
    ) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      setInitialArticleSnapshot(
        articleSnapshot(articleData, articleCaption, cropPreviewImage, mediaType)
      );
    });

    return () => window.cancelAnimationFrame(frame);
  }, [
    flag,
    initialArticleSnapshot,
    articleData,
    articleCaption,
    cropPreviewImage,
    mediaType,
  ]);

  const getVideoDuration = (file: File): Promise<number> =>
    new Promise((resolve) => {
      const video = document.createElement("video");
      video.addEventListener("loadedmetadata", () => resolve(video.duration));
      video.src = URL.createObjectURL(file);
    });

  const validateSlug = (slug: string) => {
    // Allow empty input (clearing the slug)
    if (!slug) {
      return "";  // No error if the slug is empty
    }

    // Check for invalid characters (only alphanumeric and hyphens allowed)
    const regex = /^[a-zA-Z0-9-]*$/;
    if (!regex.test(slug)) {
      return "Slug can only contain alphanumeric characters and hyphens.";
    }

    // Trim the slug and replace spaces with hyphens, then check the length
    const formattedSlug = slug.trim().replace(/\s+/g, '-').toLowerCase();

    if (formattedSlug.length > limits.slug) {
      return `Slug cannot be longer than ${limits.slug} characters.`;
    }

    return ""; // No errors
  };


  const getFieldValidationError = (field: keyof typeof limits, value: string): string => {
    if (field === "slug") return validateSlug(value);
    if (value.length > limits[field]) {
      return `Maximum ${limits[field]} characters allowed.`;
    }
    return "";
  };

  const handleFieldChange = (field: keyof typeof limits, value: string) => {
    if (field === "slug") {
      setIsSlugEdited(true);
      const error = validateSlug(value);
      setFieldErrors((prev) => ({ ...prev, [field]: error }));
      if (!error) {
        setArticleData((prev) => ({ ...prev, [field]: value }));
      }
      return;
    }

    // Clamp at max length so counter and stored value stay in sync (no stale error at 60/60)
    const clamped = value.slice(0, limits[field]);
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
    setArticleData((prev) => ({ ...prev, [field]: clamped }));
  };

  const handleEditSuccess = async (
    file: File | null,
    type?: "image" | "video",
    thumbnail?: string,
    editHistoryData?: EditHistory
  ) => {
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setPreviewImage(fileURL);
      setUploadImage(file);
      setMediaType(type || "image");

      if (editHistoryData) setEditHistory(editHistoryData);

      if (type === "video") {
        if (thumbnail) setVideoThumbnail(thumbnail);
        try {
          const duration = await getVideoDuration(file);
          setVideoDuration(duration);
        } catch (error) {
          console.error("Error getting video duration:", error);
        }
      } else {
        setVideoThumbnail("");
        setVideoDuration(0);
      }
    }
    setShowEditModal(false);
  };

  const removeMedia = () => {
    setPreviewImage("");
    setOriginalImage("");
    originalSetRef.current = false;
    setUploadImage(undefined);
    setMediaType(null);
    setVideoThumbnail("");
    setVideoDuration(0);
    setEditHistory(null);
  };

  const _handleCloseSettingsModel = () => setShowSettingsModal(false);

  const processVariables = (text: string, articleDataParam: any, additionalData: any) => {
    let processedText = text || "";
    processedText = processedText.replace(/{post_title}/g, articleDataParam.title || "");
    processedText = processedText.replace(/{author_name}/g, additionalData.authorName || "");
    processedText = processedText.replace(/{site_name}/g, additionalData.siteName || "");
    processedText = processedText.replace(/{published_year}/g, new Date().getFullYear().toString());
    return processedText;
  };

  const validateSeoFields = (data: ArticleType, additionalData: { authorName: string; siteName: string }) => {
    const errors: typeof fieldErrors = {};

    (["seo_title", "seo_description", "focus_keyword"] as const).forEach((field) => {
      const err = getFieldValidationError(field, data[field] || "");
      if (err) errors[field] = err;
    });

    if (!(flag === "update" && !isSlugEdited)) {
      const slugErr = validateSlug(data.slug || "");
      if (slugErr) errors.slug = slugErr;
    }

    const processedSeoTitle = processVariables(data.seo_title || "", data, additionalData);
    const processedSeoDescription = processVariables(data.seo_description || "", data, additionalData);

    if (processedSeoTitle.length > limits.seo_title) {
      errors.seo_title = `Maximum ${limits.seo_title} characters allowed (including expanded variables).`;
    }
    if (processedSeoDescription.length > limits.seo_description) {
      errors.seo_description = `Maximum ${limits.seo_description} characters allowed (including expanded variables).`;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      FlashMessage("Please fix SEO field errors before saving.", "error");
      return false;
    }

    setFieldErrors({});
    return true;
  };

  const validateArticle = async () => {
    const title = articleData.title;
    const description = articleData.description;
    if (!title || title.length > 250) {
      FlashMessage("Please enter headline (max 250 characters)", "error");
      return false;
    }
    if (!description) {
      FlashMessage("Please enter description", "error");
      return false;
    }

    const additionalData = {
      authorName: userName,
      siteName: "employHER",
    };
    return validateSeoFields(articleData, additionalData);
  };

  const captionAsHtml = (caption: string) => {
    const escapedCaption = caption
      .trim()
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\n/g, "<br />");

    return escapedCaption ? `<p>${escapedCaption}</p>` : "";
  };

  const openShareArticleModal = async () => {
    const isValid = await validateArticle();
    if (isValid) setShowShareModal(true);
  };

  const extractContentMediaFromHTML = (htmlContent: string, processed: any): MediaItem[] => {
    if (!htmlContent || !processed) return [];
    const mediaItems: MediaItem[] = [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const mediaWrappers = doc.querySelectorAll("[data-media-id]");

    mediaWrappers.forEach((wrapper, index) => {
      const mediaId = wrapper.getAttribute("data-media-id");
      const mediaType = wrapper.getAttribute("data-media-type") as "image" | "video";
      if (mediaId && mediaType && processed[mediaId]) {
        const mediaInfo = processed[mediaId];
        const placeholderFile = new File(
          [""],
          mediaInfo.file || `${mediaId}.${mediaType === "image" ? "jpg" : "mp4"}`,
          { type: mediaType === "image" ? "image/jpeg" : "video/mp4" }
        );
        mediaItems.push({
          id: mediaId,
          file: placeholderFile,
          blobUrl: mediaInfo.signedUrl,
          type: mediaType,
          insertIndex: index,
          timestamp: Date.now() - index,
          thumbnail: mediaInfo.thumbnailSignedUrl || undefined,
        });
      }
    });

    return mediaItems;
  };

  const fetchArticle = async () => {
    try {
      setIsSlugEdited(false);
      setInitialArticleSnapshot(null);
      const oldData = await httpRequest().get(API_URLS.fetchArticle(articleId));
      const responseData = oldData.data.data;

      const data: ArticleType = {
        title: responseData.title,
        description: responseData.content || "",
        seo_title: responseData.seo_title || "",
        seo_description: responseData.seo_description || "",
        focus_keyword: responseData.focus_keyword || "",
        slug: responseData.slug || "",
      };

      let coverMedia = "";
      let detectedMediaType: "image" | "video" | null = null;

      if (responseData.media_type) {
        detectedMediaType = responseData.media_type;
        coverMedia = detectedMediaType === "video" ? responseData.cover_video : responseData.cover_image;
      } else if (responseData.cover_video && responseData.cover_video.trim()) {
        detectedMediaType = "video";
        coverMedia = responseData.cover_video;
      } else if (responseData.cover_image && responseData.cover_image.trim()) {
        detectedMediaType = "image";
        coverMedia = responseData.cover_image;
      }

      if (detectedMediaType && coverMedia) {
        setPreviewImage(coverMedia);
        setMediaType(detectedMediaType);
        if (detectedMediaType === "video") {
          setVideoThumbnail(responseData.video_thumbnail || "");
          setVideoDuration(responseData.video_duration || 0);
        } else {
          setVideoThumbnail("");
          setVideoDuration(0);
        }
      } else {
        setPreviewImage("");
        setMediaType(null);
        setVideoThumbnail("");
        setVideoDuration(0);
      }

      setArticleData(data);
      const savedCaption = responseData.article_caption || responseData.post_text || "";
      setArticleCaption(savedCaption);

      const loadedErrors: typeof fieldErrors = {};
      (["seo_title", "seo_description", "focus_keyword"] as const).forEach((field) => {
        const err = getFieldValidationError(field, data[field] || "");
        if (err) loadedErrors[field] = err;
      });
      // Existing legacy slugs are allowed to remain unchanged during edits.
      if (flag !== "update") {
        const slugErr = validateSlug(data.slug || "");
        if (slugErr) loadedErrors.slug = slugErr;
      }
      if (Object.keys(loadedErrors).length > 0) {
        setFieldErrors(loadedErrors);
      }

      const processed = responseData.content_media_processed || null;
      setContentMediaProcessed(processed);

      const initialItems = extractContentMediaFromHTML(data.description, processed);
      setInitialMediaItems(initialItems);
    } catch (error) {
      console.log("error", error);
    }
  };

  const saveSetting = () => {
    setShowSettingsModal(false);
    FlashMessage("Settings updated successfully", "success");
  };

  const handleTextareaResize = (e: React.FormEvent<HTMLTextAreaElement>) => {
    e.currentTarget.style.height = "auto";
    e.currentTarget.style.height = e.currentTarget.scrollHeight + "px";
  };

  // Open modal on edit; preview loads directly in modal by DropZone changes below
  const handleEditMedia = () => {
    if (!cropPreviewImage) return;
    setShowEditModal(true);
  };

  const handleDeleteMedia = () => {
    setPreviewImage("");
    setOriginalImage("");
    originalSetRef.current = false;
    setUploadImage(undefined);
    setMediaType(null);
    setVideoThumbnail("");
    setVideoDuration(0);
    setEditHistory(null);
    setShowEditModal(false);
  };

  //   const openRelatedKeywordMenu = async (e: React.MouseEvent<HTMLElement>) => {
  //     const focusKeyword = (articleData?.focus_keyword || "").trim();
  //     if (!focusKeyword) {
  //       FlashMessage("Please enter a focus keyword first", "error");
  //       return;
  //     }

  //     if (kwList.length > 0) {
  //       setKwAnchorEl(e.currentTarget);
  //       setKwLoading(false);
  //       setSelectedKw(null);
  //       return;
  //     }

  //     setKwAnchorEl(e.currentTarget);
  //     setKwLoading(true);
  //     setSelectedKw(null);

  //     try {
  //       const { data } = await httpRequest().get(
  //         `${API_URLS.getRealtedKeyword}?keyword=${encodeURIComponent(focusKeyword)}`
  //       );
  //       const list: string[] = _.get(data, "data.list") || _.get(data, "data") || [];
  //       setKwList(Array.isArray(list) ? list : []);
  //     } catch (err) {
  //       setKwList([]);
  //       FlashMessage("Failed to fetch related keywords", "error");
  //     } finally {
  //       setKwLoading(false);
  //     }
  //   };

  const closeRelatedKeywordMenu = () => {
    setKwAnchorEl(null);
    setSelectedKw(null);
  };

  const applySelectedKeyword = (value?: string) => {
    const v = value ?? selectedKw;
    if (!v) return;
    setArticleData((prev) => ({ ...prev, focus_keyword: v }));
    closeRelatedKeywordMenu();
  };

  const handleSEOTitleGenerate = () => {
    setSeoGeneratorType("title");
    setShowSEOAIGenerator(true);
  };

  const handleSEODescriptionGenerate = () => {
    setSeoGeneratorType("description");
    setShowSEOAIGenerator(true);
  };

  const handleSEOContentSelect = (content: string) => {
    const field = seoGeneratorType === "title" ? "seo_title" : "seo_description";
    handleFieldChange(field, content);
    setShowSEOAIGenerator(false);
  };

  const openVariableMenu = (event: React.MouseEvent<HTMLElement>, fieldType: "seo_title" | "seo_description") => {
    setVariableMenuAnchor(event.currentTarget);
    setActiveVariableField(fieldType);
    setSelectedVariableKey(null);
  };

  const closeVariableMenu = () => {
    setVariableMenuAnchor(null);
    setActiveVariableField(null);
    setSelectedVariableKey(null);
  };

  const availableVariables = useMemo(
    () => [
      { key: "post_title", label: "Post Title", value: articleData.title || "" },
      { key: "author_name", label: "Author Name", value: userName.trim() },
      { key: "site_name", label: "Site Name", value: "employHER" },
      { key: "published_year", label: "Published Year", value: new Date().getFullYear().toString() },
    ],
    [articleData.title, userName]
  );

  const insertVariable = (_variableKey: string, variableValue: string) => {
    if (!activeVariableField) return;
    const insertText = (variableValue || "").trim();
    if (!insertText) return;

    const currentValue =
      activeVariableField === "seo_title"
        ? articleData.seo_title || ""
        : articleData.seo_description || "";
    const newValue = currentValue ? `${currentValue} ${insertText}` : insertText;
    handleFieldChange(activeVariableField, newValue);
    closeVariableMenu();
  };

  const confirmInsertVariable = () => {
    if (!selectedVariableKey) return;
    const variable = availableVariables.find((v) => v.key === selectedVariableKey);
    if (!variable) return;
    insertVariable(variable.key, variable.value);
  };

  const saveArticle = async () => {
    setLoading(true);
    const isValid = await validateArticle();
    if (!isValid) {
      setLoading(false);
      return;
    }

    try {
      let payload = new FormData();
      const additionalData = {
        authorName: userName,
        siteName: "employHER",
      };

      const processedSeoTitle = processVariables(articleData.seo_title || "", articleData, additionalData);
      const processedSeoDescription = processVariables(articleData.seo_description || "", articleData, additionalData);

      payload.append("title", articleData.title || "");
      payload.append("description", articleData.description || "");
      payload.append("seo_title", processedSeoTitle);
      payload.append("seo_description", processedSeoDescription);
      payload.append("focus_keyword", articleData.focus_keyword || "");
      // Avoid resubmitting a legacy slug that exceeds today's length limit.
      if (flag === "create" || isSlugEdited) {
        payload.append("slug", articleData.slug || "");
      }
      // The article body remains in `description`; this optional field is the
      // short message displayed above its preview card in the home feed.
      // A newly created article receives this value from a plain textarea,
      // whereas an existing article receives the already-formatted caption
      // from the API. Re-escaping the latter makes tags visible in the feed.
      payload.append(
        "post_text",
        flag === "create" ? captionAsHtml(articleCaption) : articleCaption
      );

      if (uploadImage && uploadImage instanceof File) {
        if (mediaType === "video") {
          payload.append("cover_video", uploadImage);
          payload.append("media_type", "video");

          if (videoThumbnail && videoDuration) {
            payload.append("video_duration", videoDuration.toString());
            try {
              const thumbnailResponse = await fetch(videoThumbnail);
              const thumbnailBlob = await thumbnailResponse.blob();
              const thumbnailFile = new File([thumbnailBlob], "video-thumbnail.jpg", { type: "image/jpeg" });
              payload.append("video_thumbnail", thumbnailFile);
            } catch (error) {
              console.error("Error processing video thumbnail:", error);
            }
          }
        } else {
          payload.append("cover_image", uploadImage);
          payload.append("media_type", "image");
        }
      }

      if (contentMediaItems.length > 0) {
        const validMediaItems = contentMediaItems.filter((item) => item.file instanceof File && item.id && item.type);
        if (validMediaItems.length > 0) {
          payload.append("content_media_count", validMediaItems.length.toString());
          const mediaMapping = validMediaItems.map((item) => ({
            id: item.id,
            type: item.type,
          }));
          payload.append("content_media_mapping", JSON.stringify(mediaMapping));

          for (const item of validMediaItems) {
            try {
              payload.append(`content_media_${item.id}`, item.file);
              if (item.type === "video" && item.thumbnail) {
                try {
                  const thumbResp = await fetch(item.thumbnail);
                  const thumbBlob = await thumbResp.blob();
                  const thumbFile = new File([thumbBlob], `thumbnail-${item.id}.jpg`, { type: "image/jpeg" });
                  payload.append(`content_media_${item.id}_thumbnail`, thumbFile);
                } catch (thumbnailError) {
                  console.error(`Error processing thumbnail for ${item.id}:`, thumbnailError);
                }
              }
            } catch (error) {
              console.error(`Error processing media item ${item.id}:`, error);
            }
          }
        }
      }

      if (typeof prepareFormDataCallback === "function") {
        payload = prepareFormDataCallback(payload);
      }

      const apiCall =
        flag === "create"
          ? httpRequest().post<{ data: ArticleType }>(API_URLS.article, payload)
          : httpRequest().put(API_URLS.articleUpdate(articleId), payload);

      const response = await apiCall;

      if (response.status === 200) {
        const message = flag === "create" ? "Article created successfully!" : "Article updated successfully!";
        FlashMessage(message, "success");
        navigate(appRoutes.Feed.path);
      }

      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      console.error("Error saving article:", error);
      const errors = error?.response?.data?.errors;
      if (errors && typeof errors === "object") {
        const firstErrorKey = Object.keys(errors)[0];
        const firstErrorMessage = errors[firstErrorKey];
        FlashMessage(firstErrorMessage || "Validation error occurred", "error");
      } else {
        FlashMessage(error?.message || "An error occurred while saving the article", "error");
      }
    }
  };

  const GoogleSerpPreview: React.FC<{ title: string; description: string; url: string; image?: string }> = ({
    title,
    description,
    url,
    image,
  }) => {
    const clamp = (str: string, n: number) => (str.length <= n ? str : str.slice(0, n).replace(/\s+\S*$/, "") + "…");
    return (
      <div className="google-serp">
        {image ? (
          <div className="serp-thumb">
            <img src={image} alt="" />
          </div>
        ) : null}

        <div className="serp-text">
          <div className="serp-title" title={title}>
            {clamp(title || "Title", 70)}
          </div>
          <div className="serp-url" title={url}>
            {url}
          </div>
          <div className="serp-desc" title={description}>
            {clamp(description || "Descption", 155)}
          </div>
        </div>
      </div>
    );
  };

  const serpUrl = useMemo(() => {
    const host = window.location.host || "www.employher.com";
    const protocol = window.location.protocol || "https:";
    const cleanSlug = (articleData.slug || "").toString().replace(/^\//, "");
    return `${protocol}//${host}/${cleanSlug}`;
  }, [articleData.slug]);
  const openRelatedKeywordMenu = async (e: React.MouseEvent<HTMLElement>) => {
    const focusKeyword = (articleData?.focus_keyword || "").trim();
    if (!focusKeyword) {
      FlashMessage("Please enter a focus keyword first", "error");
      return;
    }

    // Always fetch new keywords (removed the condition that was preventing regeneration)
    setKwAnchorEl(e.currentTarget);
    setKwLoading(true);
    setSelectedKw(null);
    setKwList([]); // Clear existing keywords

    try {
      const { data } = await httpRequest().get(
        `${API_URLS.getRealtedKeyword}?keyword=${encodeURIComponent(focusKeyword)}`
      );
      const list: string[] = _.get(data, "data.list") || _.get(data, "data") || [];
      setKwList(Array.isArray(list) ? list : []);
    } catch (err) {
      setKwList([]);
      FlashMessage("Failed to fetch related keywords", "error");
    } finally {
      setKwLoading(false);
    }
  };

  // Add a new function specifically for regenerating keywords:
  const regenerateRelatedKeywords = async (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();

    const focusKeyword = (articleData?.focus_keyword || "").trim();
    if (!focusKeyword) {
      FlashMessage("Please enter a focus keyword first", "error");
      return;
    }

    setKwLoading(true);
    setSelectedKw(null);
    setKwList([]); // Clear existing keywords

    try {
      const { data } = await httpRequest().get(
        `${API_URLS.getRealtedKeyword}?keyword=${encodeURIComponent(focusKeyword)}`
      );
      const list: string[] = _.get(data, "data.list") || _.get(data, "data") || [];
      setKwList(Array.isArray(list) ? list : []);
    } catch (err) {
      setKwList([]);
      FlashMessage("Failed to fetch related keywords", "error");
    } finally {
      setKwLoading(false);
    }
  };


  return (
    <>
      <div className="article-main-container">
        <div className="article-container">
          <div className="article-wrapper">
            <div className="form-group article-title">
              <textarea
                ref={textareaRef}
                className="form-control"
                id="title"
                maxLength={250}
                placeholder="Headline"
                onInput={handleTextareaResize}
                rows={1}
                cols={30}
                value={articleData?.title || ""}
                onChange={(e) => {
                  const data = e.currentTarget.value || "";
                  setArticleData((prev) => ({ ...prev, title: data }));
                  handleTextareaResize(e);
                }}
                style={{ overflow: "hidden", resize: "none" }}
              />
            </div>

            <div className="article-image">
              <div className="article-input-type-file">
                {cropPreviewImage ? (
                  <div className="article-src">
                    {mediaType === "video" ? (
                      <div
                        className="video-preview-container"
                        style={{ background: `url(${videoThumbnail || cropPreviewImage}) center/cover no-repeat` }}
                      >
                        <video src={cropPreviewImage} poster={videoThumbnail || undefined} controls className="article-video" />
                      </div>
                    ) : (
                      <div className="image-preview-wrapper">
                        <Image src={cropPreviewImage} alt="Cover image" />
                      </div>
                    )}

                    <div className="media-action-buttons">
                      {mediaType === "image" && (
                        <span className="article-media-edit-button" onClick={handleEditMedia} title="Edit image">
                          <Edit className="image-edit-icon" />
                        </span>
                      )}
                      <span className="media-delete-button" onClick={removeMedia} title="Delete media">
                        <Delete className="delete-icon" />
                      </span>
                    </div>
                  </div>
                ) : (
                  <ArticleDropZone
                    allowFileTypes={["png", "jpg", "jpeg", "gif", "webp", "mp4", "mov", "avi", "webm", "mkv"]}
                    allowFileSize={50 * 1024 * 1024}
                    onSuccess={(file, originalFile, type, thumbnail, editHistoryData) =>
                      (async () => {
                        const f = file as File;
                        const of = originalFile as File | null;
                        if (!f) return;
                        const fileURL = URL.createObjectURL(f);
                        setPreviewImage(fileURL);
                        setUploadImage(f);
                        setMediaType((type as "image" | "video") || "image");

                        if (of instanceof File) {
                          const originalURL = URL.createObjectURL(of);
                          setOriginalImage(originalURL);
                        } else {
                          setOriginalImage(fileURL);
                        }

                        setEditHistory((editHistoryData as EditHistory) || null);

                        if (type === "video") {
                          if (thumbnail) setVideoThumbnail(thumbnail);
                          try {
                            const duration = await getVideoDuration(f);
                            setVideoDuration(duration);
                          } catch (err) {
                            console.error("Error getting video duration:", err);
                            setVideoDuration(0);
                          }
                        } else {
                          setVideoThumbnail("");
                          setVideoDuration(0);
                        }
                      })()
                    }
                    onError={(error) => FlashMessage(error.message, "error")}
                  />
                )}
              </div>
            </div>

            <ArticleTextEditor
              name="description"
              placeholder="Start writing..."
              value={articleData?.description || ""}
              onChange={(value) => setArticleData((prev) => ({ ...prev, description: value || "" }))}
              onMediaChange={setContentMediaItems}
              onPrepareFormData={setPrepareFormDataCallback}
              initialMediaItems={initialMediaItems}
              contentMediaProcessed={contentMediaProcessed}
              autoGrow
            />

            <div className="btn-wrapper">
              <Button color="secondary" className="seo-settings-btn" onClick={() => setShowSettingsModal(true)}>
                SEO Settings
              </Button>
              <Button
                loading={loading}
                onClick={flag === "create" ? openShareArticleModal : saveArticle}
                disabled={
                  loading ||
                  Object.values(fieldErrors).some((msg) => Boolean(msg)) ||
                  !articleData.title ||
                  !articleData.description ||
                  !isArticleDirty
                }
              >
                {flag === "create" ? "Post" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        className="article-share-modal"
        visible={showShareModal}
        closeButtonCross
        closeOnBackDrop={!loading}
        onClose={() => !loading && setShowShareModal(false)}
      >
        <div className="article-share-dialog">
          <div className="article-share-author">
            <Avatar
              src={_.get(tmpCurrentUser, "profile_image", "")}
              type="candidate"
              className="article-share-avatar"
            />
            <div>
              <h2>{userName.trim() || "Your profile"}</h2>
              <p>Post to Anyone</p>
            </div>
          </div>

          <textarea
            className="article-share-caption"
            value={articleCaption}
            maxLength={3000}
            onChange={(event) => setArticleCaption(event.target.value)}
            placeholder="Tell your network what your article is about..."
            aria-label="Article introduction for your feed"
          />

          <div className="article-share-preview" aria-label="Article preview">
            {cropPreviewImage && (
              mediaType === "video" ? (
                <div className="article-share-video-cover">
                  <Image src={videoThumbnail || cropPreviewImage} alt="Article video cover" />
                  <span className="play-icon-cover"><i className="fa fa-play" /></span>
                </div>
              ) : (
                <Image src={cropPreviewImage} alt="Article cover" />
              )
            )}
            <div className="article-share-preview-details">
              <strong>{articleData.title}</strong>
              <span>{userName.trim()}</span>
            </div>
          </div>

          <div className="article-share-actions">
            <span>{articleCaption.length}/3000</span>
            <div>
              <Button color="secondary" disabled={loading} onClick={() => setShowShareModal(false)}>
                Back to article
              </Button>
              <Button loading={loading} onClick={saveArticle}>Publish</Button>
            </div>
          </div>
        </div>
      </Modal>

      {showEditModal && cropPreviewImage && (
        <ArticleDropZone
          allowFileTypes={["png", "jpg", "jpeg", "gif", "webp"]}
          allowFileSize={10 * 1024 * 1024}
          existingImage={cropPreviewImage}
          originalImage={originalImage || cropPreviewImage}
          existingEditHistory={editHistory || undefined}
          autoOpenEdit={false}            // ✅ open in preview view (not edit tools)
          onSuccess={(file, _originalFile, type, thumbnail, editHistoryData) =>
            handleEditSuccess(file as File, type, thumbnail, (editHistoryData as EditHistory) || undefined)
          }
          onError={(error) => FlashMessage(error.message, "error")}
          onClose={() => setShowEditModal(false)}
          onDelete={handleDeleteMedia}

        />
      )}

      <Modal
        className="article-settings"
        visible={showSettingsModal}
        title="SEO Settings"
        size="large"
        onClose={_handleCloseSettingsModel}
        width="807px"
      >
        <div className="seo-settings-container">
          <div className="seo-settings-scrollable">
          <div className="seo-description">
            <h4>Search Engine Optimization</h4>
            <p className="sub-description">
              Specify this page's title and description. You can see how they'll look in search engine results page (SERPs) in the preview below.
            </p>
          </div>

          <div className="seo-fields-section">
            <div className="form-group">
              <p className="field-description">Enter the main keyword or phrase you want this post to rank for in search engines.</p>
              <div className="label-row">
                <label>Focus Keyword</label>
                <div className="label-actions">
                  <Button
                    type="button"
                    className={`insert-variable-btn ${!articleData?.focus_keyword?.trim() ? "disabled" : ""}`}
                    onClick={openRelatedKeywordMenu}
                    disabled={!articleData?.focus_keyword?.trim()}
                  >
                    Get related Keyword
                  </Button>
                </div>
              </div>

              <Input
                name="focus_keyword"
                className="seo-input"
                type="text"
                placeholder="Ex: Career tips for freshers"
                value={articleData?.focus_keyword || ""}
                onChange={(e) => handleFieldChange("focus_keyword", e.currentTarget.value)}
              />

              <div className="seo-help-row">
                <div className="seo-help-left">
                  {fieldErrors.focus_keyword && <div className="error-message">{fieldErrors.focus_keyword}</div>}
                </div>
                <span className="character-count">{articleData.focus_keyword?.length || 0}/60</span>
              </div>
            </div>

            <div className="form-group">
              <p className="field-description">This is what will appear when this post shows up in the search results.</p>
              <div className="label-row">
                <label>SEO Title</label>
                <div className="label-actions">
                  <div className="ai-generate-btn" onClick={handleSEOTitleGenerate} style={{ cursor: "pointer" }}>
                    <img src={aiGenrateIcon} alt="" className="ai-img" />
                  </div>
                  <Button type="button" className="insert-variable-btn" onClick={(e) => openVariableMenu(e, "seo_title")}>
                    Insert Variable
                  </Button>
                </div>
              </div>

              <Input
                name="seo_title"
                className="seo-input"
                type="text"
                placeholder="Enter SEO title"
                value={articleData?.seo_title || ""}
                onChange={(e) => handleFieldChange("seo_title", e.currentTarget.value)}
              />
              <div className="seo-help-row">
                <div className="seo-help-left">
                  {fieldErrors.seo_title && <div className="error-message">{fieldErrors.seo_title}</div>}
                </div>
                <span className="character-count">{articleData.seo_title?.length || 0}/60</span>
              </div>
            </div>

            <div className="form-group">
              <p className="field-description">This is the unique URL of this page, displayed under the post title in search results.</p>
              <label>Slug</label>

              <Input
                name="slug"
                className="seo-input"
                type="text"
                placeholder="Ex: career-tips-for-freshers"
                value={articleData?.slug || ""}
                // onChange={(e) => handleFieldChange("slug", e.currentTarget.value)}
                onChange={(e) => handleFieldChange("slug", e.target.value)}

              />
              <div className="seo-help-row">
                <div className="seo-help-left">
                  {fieldErrors.slug && <div className="error-message">{fieldErrors.slug}</div>}
                </div>
                <span className="character-count">{articleData.slug?.length || 0}/75</span>
              </div>
            </div>

            <div className="form-group">
              <p className="field-description">
                This is what will appear as the description when this post shows up in the search results.
              </p>
              <div className="label-row">
                <label>SEO Description</label>
                <div className="label-actions">
                  <div className="ai-generate-btn" onClick={handleSEODescriptionGenerate} style={{ cursor: "pointer" }}>
                    <img src={aiGenrateIcon} alt="" className="ai-img" />
                  </div>
                  <Button type="button" className="insert-variable-btn" onClick={(e) => openVariableMenu(e, "seo_description")}>
                    Insert Variable
                  </Button>
                </div>
              </div>

              <textarea
                name="seo_description"
                className="seo-textarea"
                placeholder="Search engines may show a different description"
                value={articleData?.seo_description || ""}
                onChange={(e) => handleFieldChange("seo_description", e.currentTarget.value)}
              />
              <div className="seo-help-row">
                <div className="seo-help-left">
                  {fieldErrors.seo_description && <span className="error-message">{fieldErrors.seo_description}</span>}
                </div>
                <span className="character-count">{articleData.seo_description?.length || 0}/160</span>
              </div>
            </div>
          </div>

          <div className="seo-preview-section">
            <h6>Preview on Google</h6>
            <GoogleSerpPreview
              title={articleData.seo_title || articleData.title || "Title"}
              description={articleData.seo_description || "Description"}
              url={serpUrl}
              image={(mediaType === "video" ? videoThumbnail || cropPreviewImage : cropPreviewImage) || undefined}
            />
          </div>
          </div>

          <div className="seo-actions">
            <Button color="secondary" onClick={_handleCloseSettingsModel}>
              Cancel
            </Button>
            <Button onClick={saveSetting}>Save</Button>
          </div>
        </div>
      </Modal>

      {/* <Menu anchorEl={kwAnchorEl} open={Boolean(kwAnchorEl)} onClose={closeRelatedKeywordMenu} className="menu-option-wrapper">
        {kwLoading && <MenuItem disabled>Loading…</MenuItem>}
        {!kwLoading && kwList.length === 0 && <MenuItem disabled>No suggestions</MenuItem>}
        {!kwLoading &&
          kwList.map((kw) => (
            <MenuItem key={kw} selected={selectedKw === kw} onClick={() => setSelectedKw(kw)}>
              {kw}
            </MenuItem>
          ))}
        {!kwLoading && (
          <MenuItem disableRipple disabled={false} className="related-button-menu" onClick={(e) => e.stopPropagation()}>
            <div className="related-drop-down">
              <Button
                color="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  openRelatedKeywordMenu(e as any);
                }}
              >
                Regenerate
              </Button>
              <Button
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  applySelectedKeyword();
                }}
                disabled={!selectedKw}
              >
                Add
              </Button>
            </div>
          </MenuItem>
        )}
      </Menu> */}


      <Menu
        anchorEl={kwAnchorEl}
        open={Boolean(kwAnchorEl)}
        onClose={closeRelatedKeywordMenu}
        className="keyword-menu-wrapper"
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {kwLoading && <MenuItem disabled>Loading…</MenuItem>}
        {!kwLoading && kwList.length === 0 && <MenuItem disabled>No suggestions</MenuItem>}
        {!kwLoading &&
          kwList.map((kw) => (
            <MenuItem key={kw} selected={selectedKw === kw} onClick={() => applySelectedKeyword(kw)}>
              {kw}
            </MenuItem>
          ))}
        {!kwLoading && (
          <MenuItem disableRipple disabled={false} className="related-button-menu" onClick={(e) => e.stopPropagation()}>
            <div className="related-drop-down">
              <Button
                color="secondary"
                onClick={regenerateRelatedKeywords} // Use the new function
              >
                Regenerate
              </Button>
              <Button
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  applySelectedKeyword();
                }}
                disabled={!selectedKw}
              >
                Add
              </Button>
            </div>
          </MenuItem>
        )}
      </Menu>

      <Menu
        anchorEl={variableMenuAnchor}
        open={Boolean(variableMenuAnchor)}
        onClose={closeVariableMenu}
        className="insert-variable-menu-wrapper"
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {availableVariables.map((variable) => (
          <MenuItem
            key={variable.key}
            selected={selectedVariableKey === variable.key}
            onClick={() => setSelectedVariableKey(variable.key)}
          >
            {variable.label}
          </MenuItem>
        ))}
        <MenuItem disableRipple disabled={false} className="related-button-menu" onClick={(e) => e.stopPropagation()}>
          <div className="related-drop-down">
            <Button color="secondary" onClick={closeVariableMenu}>
              Cancel
            </Button>
            <Button color="primary" onClick={confirmInsertVariable} disabled={!selectedVariableKey}>
              Insert Variable
            </Button>
          </div>
        </MenuItem>
      </Menu>

      <SEOAIGenerator
        visible={showSEOAIGenerator}
        onClose={() => setShowSEOAIGenerator(false)}
        onSelectContent={handleSEOContentSelect}
        type={seoGeneratorType}
        currentContent={seoGeneratorType === "title" ? articleData.seo_title : articleData.seo_description}
        focusKeyword={articleData.focus_keyword}
        articleTitle={articleData.title}
      />
    </>
  );
}

export default Article;
